import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';

import { adminApi } from '../services/api.jsx';
import {
  annoncerErreur, annoncerSucces, Bandeau, confirmer, EtatErreur, EtatSansDonnees, SqueletteTableau,
} from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   BARÈME DES GRADES DE COACH

   Le seuil qui sépare un coach junior d'un senior est une décision de
   politique interne. Il se règle donc ici, par ceux qui la prennent, sans
   livraison.

   Deux garde-fous que l'interface rend visibles :

   - Le grade est FIGÉ au moment de la décision sur une candidature.
     Modifier un seuil ne reclasse personne rétroactivement — un senior ne
     redevient pas junior parce qu'on a relevé la barre.
   - Un grade déjà attribué n'est pas supprimé mais désactivé. Effacer la
     ligne mettrait à nul le classement des coachs concernés, et le
     classement d'un intervenant en service n'est pas une donnée de schéma.

   L'aperçu en bas donne la lecture inverse du tableau : à quel grade
   aboutit une ancienneté donnée. C'est la question qu'on se pose vraiment
   en réglant un seuil, et la table ordonnée par seuil décroissant n'y
   répond pas d'un coup d'œil.
   ═══════════════════════════════════════════════════════════════════════════ */

const ANCIENNETES_TEMOINS = [0, 1, 2, 3, 5, 8, 12, 20];

export default function BaremeGrades({ peutModifier }) {
  // `null` tant que rien n'est arrivé : seul ce premier chargement montre
  // un squelette, les rechargements après une action gardent le tableau.
  const [grades, setGrades] = useState(null);
  const [erreurChargement, setErreurChargement] = useState(null);
  // Erreur de saisie (seuil, nouveau grade) : elle reste dans le panneau.
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(null);
  const [nouveau, setNouveau] = useState(null);

  const charger = useCallback(async () => {
    try {
      const donnees = await adminApi.getGrades();
      setGrades(Array.isArray(donnees) ? donnees : []);
      setErreurChargement(null);
    } catch (e) {
      setErreurChargement(e);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const enregistrerSeuil = async (grade, valeur) => {
    const seuil = Number(valeur);
    if (!Number.isInteger(seuil) || seuil < 0 || seuil > 70) {
      setErreur('Le seuil doit être un nombre entier entre 0 et 70 ans.');
      return;
    }
    setEnCours(grade.id);
    setErreur('');
    try {
      await adminApi.majGrade(grade.id, { annees_minimum: seuil });
      await charger();
      annoncerSucces(`« ${grade.libelle} » s'applique désormais à partir de ${seuil} an(s).`);
    } catch (e) {
      setErreur(e.message || "Le seuil n'a pas pu être enregistré.");
    } finally {
      setEnCours(null);
    }
  };

  const creer = async () => {
    if (!nouveau?.libelle?.trim()) return;
    setEnCours('nouveau');
    setErreur('');
    try {
      await adminApi.creerGrade({
        libelle: nouveau.libelle.trim(),
        annees_minimum: Number(nouveau.annees_minimum) || 0,
        description: (nouveau.description || '').trim(),
      });
      setNouveau(null);
      await charger();
      annoncerSucces('Grade ajouté au barème.');
    } catch (e) {
      setErreur(e.message || "Le grade n'a pas pu être ajouté.");
    } finally {
      setEnCours(null);
    }
  };

  const retirer = async (grade) => {
    const attribue = grade.coachs_classes > 0;
    const question = attribue
      ? `« ${grade.libelle} » est attribué à ${grade.coachs_classes} coach(s). `
        + 'Il sera désactivé — leur classement est conservé. Continuer ?'
      : `Supprimer « ${grade.libelle} » du barème ?`;
    if (!await confirmer(question)) return;

    setEnCours(grade.id);
    try {
      const reponse = await adminApi.supprimerGrade(grade.id);
      await charger();
      annoncerSucces(reponse?.detail || 'Grade retiré du barème.');
    } catch (e) {
      annoncerErreur(e.message || "Le grade n'a pas pu être retiré.");
    } finally {
      setEnCours(null);
    }
  };

  /* Reproduit la règle du serveur : on retient le grade actif dont le seuil
     est le plus haut parmi ceux atteints. */
  const gradePour = (annees) => {
    const eligibles = (grades || [])
      .filter((g) => g.actif && g.annees_minimum <= annees)
      .sort((a, b) => b.annees_minimum - a.annees_minimum);
    return eligibles[0]?.libelle || '—';
  };

  if (grades === null) {
    return erreurChargement
      ? <EtatErreur erreur={erreurChargement} onReessayer={charger} />
      : <SqueletteTableau colonnes={4} lignes={4} />;
  }

  return (
    <div className="space-y-5">
      <div className="panneau p-6">
        <h3 className="titre-carte">Barème des grades</h3>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-1.5 mb-5 max-w-2xl leading-relaxed">
          L'ancienneté déclarée par un candidat détermine son grade à
          l'acceptation. Modifier un seuil n'affecte que les décisions à venir :
          les coachs déjà classés conservent leur grade.
        </p>

        {erreur && (
          <div role="alert" className="encart encart-alerte mb-4">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{erreur}</span>
          </div>
        )}
        {erreurChargement && (
          <div className="mb-4">
            <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={charger}>Réessayer</button>}>
              L’actualisation a échoué : ce barème est peut-être ancien.
            </Bandeau>
          </div>
        )}

        {grades.length === 0 ? (
          <EtatSansDonnees cas={peutModifier ? 'premiere' : 'vide'} compact
                           titre="Aucun grade au barème"
                           aide={peutModifier
                             ? 'Ajoutez le premier grade et son seuil d’ancienneté.'
                             : 'L’administration n’a pas encore fixé de grade.'} />
        ) : (
        <div className="tableau-hote">
          <table className="tableau-cartes w-full text-[13.5px]">
            <thead>
              <tr className="entete-tableau text-left">
                <th className="rounded-l-[8px]">Grade</th>
                <th className="w-[190px]">À partir de</th>
                <th className="w-[120px]">Coachs classés</th>
                <th className="w-[80px] rounded-r-[8px]" />
              </tr>
            </thead>
            <tbody>
              {grades.map((g) => (
                <tr key={g.id} className="border-b border-[var(--border)]"
                    style={{ opacity: g.actif ? 1 : 0.5 }}>
                  <td data-libelle="Grade" className="px-4 py-3">
                    <div className="font-medium text-[var(--text-bold)]">
                      {g.libelle}
                      {!g.actif && (
                        <span className="ml-2 text-[11.5px] font-normal text-[var(--text-muted)]">
                          (désactivé)
                        </span>
                      )}
                    </div>
                    {g.description && (
                      <div className="text-[12.5px] text-[var(--text-muted)] mt-0.5
                                      leading-relaxed max-w-md">
                        {g.description}
                      </div>
                    )}
                  </td>
                  <td data-libelle="À partir de" className="px-4 py-3">
                    {peutModifier ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="0" max="70" defaultValue={g.annees_minimum}
                          disabled={enCours === g.id}
                          onBlur={(e) => {
                            if (Number(e.target.value) !== g.annees_minimum) {
                              enregistrerSeuil(g, e.target.value);
                            }
                          }}
                          style={{ minHeight: 44, width: 90 }}
                          className="champ tabular-nums"
                        />
                        <span className="text-[13px] text-[var(--text-muted)]">an(s)</span>
                        {enCours === g.id && (
                          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                        )}
                      </div>
                    ) : (
                      <span className="tabular-nums">{g.annees_minimum} an(s)</span>
                    )}
                  </td>
                  <td data-libelle="Coachs classés" className="px-4 py-3 tabular-nums text-[var(--text-main)]">
                    {g.coachs_classes}
                  </td>
                  <td data-actions="" className="px-4 py-3">
                    {peutModifier && (
                      <button type="button" onClick={() => retirer(g)}
                              disabled={enCours === g.id}
                              aria-label={`Retirer ${g.libelle}`}
                              style={{ minHeight: 44 }}
                              className="act act-tertiaire">
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {peutModifier && (nouveau ? (
          <div className="mt-4 panneau p-4 space-y-3" style={{ background: 'var(--bg-main)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
              <input type="text" autoFocus placeholder="Intitulé du grade"
                     value={nouveau.libelle}
                     onChange={(e) => setNouveau({ ...nouveau, libelle: e.target.value })}
                     style={{ minHeight: 44 }}
                     className="champ" />
              <input type="number" min="0" max="70" placeholder="Seuil"
                     value={nouveau.annees_minimum}
                     onChange={(e) => setNouveau({ ...nouveau, annees_minimum: e.target.value })}
                     style={{ minHeight: 44 }}
                     className="champ tabular-nums" />
            </div>
            <input type="text" placeholder="Description (facultative)"
                   value={nouveau.description || ''}
                   onChange={(e) => setNouveau({ ...nouveau, description: e.target.value })}
                   style={{ minHeight: 44 }}
                   className="champ" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setNouveau(null)} className="act act-contour">
                Annuler
              </button>
              <button type="button" onClick={creer}
                      disabled={enCours === 'nouveau' || !nouveau.libelle.trim()}
                      aria-busy={enCours === 'nouveau' || undefined}
                      className="act">
                {enCours === 'nouveau'
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : 'Ajouter'}
              </button>
            </div>
          </div>
        ) : (
          <button type="button"
                  onClick={() => setNouveau({ libelle: '', annees_minimum: 0, description: '' })}
                  className="act act-contour mt-4">
            <Plus className="w-4 h-4" aria-hidden="true" />
            Ajouter un grade
          </button>
        ))}
      </div>

      {/* Lecture inverse : à quel grade aboutit une ancienneté donnée. C'est
          la question qu'on se pose en réglant un seuil, et la table ordonnée
          par seuil décroissant n'y répond pas d'un coup d'œil. */}
      <div className="panneau p-6">
        <h3 className="titre-carte">Ce que le barème donnerait</h3>
        <p className="text-[12.5px] text-[var(--text-muted)] mt-1.5 mb-4">
          Grade attribué à une candidature déposée aujourd'hui, selon
          l'ancienneté déclarée.
        </p>
        <div className="flex flex-wrap gap-2">
          {ANCIENNETES_TEMOINS.map((annees) => (
            <div key={annees}
                 className="rounded-pastille border border-[var(--border)] px-3.5 py-2.5"
                 style={{ background: 'var(--bg-main)' }}>
              <div className="text-[11.5px] text-[var(--text-muted)] tabular-nums">
                {annees} an{annees > 1 ? 's' : ''}
              </div>
              <div className="text-[13px] font-medium text-[var(--text-bold)] mt-0.5">
                {gradePour(annees)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
